import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import "../../../css/cookieConsent.css";

const STORAGE_KEY = "BV_COOKIE_CONSENT";
const NOTICE_COOKIE = "BV_COOKIE_NOTICE";
const LEAVE_MS = 420;

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistConsent = (choice) => {
  const payload = {
    choice,
    analytics: choice === "all",
    marketing: choice === "all",
    essential: true,
    at: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  Cookies.set(NOTICE_COOKIE, choice, { expires: 365, sameSite: "Lax" });
  return payload;
};

const PREF_ITEMS = [
  {
    id: "essential",
    title: "Essential",
    desc: "Login, security & bookings",
    locked: true,
  },
  {
    id: "analytics",
    title: "Analytics",
    desc: "Site performance & usage",
    locked: false,
    key: "analyticsOn",
  },
  {
    id: "marketing",
    title: "Marketing",
    desc: "Offers & promotions",
    locked: false,
    key: "marketingOn",
  },
];

export default function CookieConsent() {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(true);
  const [marketingOn, setMarketingOn] = useState(false);

  const toggles = { analyticsOn, marketingOn };
  const setters = {
    analyticsOn: setAnalyticsOn,
    marketingOn: setMarketingOn,
  };

  useEffect(() => {
    const existing = readConsent() || Cookies.get(NOTICE_COOKIE);
    if (existing) return undefined;

    const showTimer = window.setTimeout(() => {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    }, 500);

    return () => window.clearTimeout(showTimer);
  }, []);

  const dismiss = useCallback((choice) => {
    persistConsent(choice);
    setLeaving(true);
    setVisible(false);
    window.setTimeout(() => setMounted(false), LEAVE_MS);
  }, []);

  const handleAcceptAll = () => dismiss("all");
  const handleRejectAll = () => dismiss("essential");

  const handleSavePrefs = () => {
    const choice = analyticsOn || marketingOn ? "all" : "essential";
    dismiss(choice);
  };

  if (!mounted) return null;

  const rootClass = [
    "bv-cookie-consent",
    visible && !leaving ? "is-visible" : "",
    leaving ? "is-leaving" : "",
    settingsOpen ? "has-settings-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bv-cookie-title"
      aria-describedby="bv-cookie-desc"
    >
      <div
        className="bv-cookie-consent__backdrop"
        aria-hidden="true"
        onClick={handleRejectAll}
      />

      <div className="bv-cookie-consent__panel">
        <div className="bv-cookie-consent__shell">
          <div className="bv-cookie-consent__hero">
            <div className="bv-cookie-consent__body">
              <span className="bv-cookie-consent__badge">
                <i className="fa-solid fa-cookie-bite" aria-hidden="true" />
                Cookies
              </span>
              <h2 id="bv-cookie-title" className="bv-cookie-consent__title">
                Your experience matters to us
              </h2>
              <p id="bv-cookie-desc" className="bv-cookie-consent__text">
                We use cookies to keep Book Venue safe, fast, and tailored for you.
                Choose how we use optional cookies, or accept all to continue.
              </p>
              <p className="bv-cookie-consent__links">
                <Link to="/privacy-policy">Privacy Policy</Link>
                <span aria-hidden="true">·</span>
                <Link to="/terms-and-conditions">Terms</Link>
              </p>
            </div>

            <div className="bv-cookie-consent__actions" role="group" aria-label="Cookie choice">
              <button
                type="button"
                className="bv-cookie-consent__btn bv-cookie-consent__btn--reject"
                onClick={handleRejectAll}
              >
                Reject all
              </button>
              <button
                type="button"
                className="bv-cookie-consent__btn bv-cookie-consent__btn--accept"
                onClick={handleAcceptAll}
              >
                Accept all
              </button>
              <button
                type="button"
                className={`bv-cookie-consent__btn bv-cookie-consent__btn--settings${settingsOpen ? " is-active" : ""}`}
                onClick={() => setSettingsOpen((o) => !o)}
                aria-expanded={settingsOpen}
                aria-controls="bv-cookie-settings"
              >
                <i className="fa-solid fa-sliders" aria-hidden="true" />
                Settings
                <i className="fa-solid fa-chevron-down bv-cookie-consent__chev" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div
            id="bv-cookie-settings"
            className={`bv-cookie-consent__settings${settingsOpen ? " is-open" : ""}`}
          >
            <div className="bv-cookie-consent__settings-inner">
              <div
                className="bv-cookie-consent__prefs-bar"
                role="group"
                aria-label="Cookie categories"
              >
                {PREF_ITEMS.map((item, index) => {
                  const isOn = item.key ? toggles[item.key] : true;
                  const segClass = [
                    "bv-cookie-consent__seg",
                    isOn ? "is-on" : "",
                    item.locked ? "is-locked" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <div
                      key={item.id}
                      className={segClass}
                      title={item.desc}
                    >
                      <span className="bv-cookie-consent__seg-name">
                        {item.title}
                      </span>
                      {item.locked ? (
                        <span className="bv-cookie-consent__seg-tag">
                          Required
                        </span>
                      ) : (
                        <button
                          type="button"
                          className={`bv-cookie-consent__switch${isOn ? " is-on" : ""}`}
                          role="switch"
                          aria-checked={isOn}
                          aria-label={`${item.title}: ${item.desc}`}
                          onClick={() => setters[item.key]((v) => !v)}
                        />
                      )}
                      {index < PREF_ITEMS.length - 1 ? (
                        <span className="bv-cookie-consent__seg-divider" aria-hidden="true" />
                      ) : null}
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="bv-cookie-consent__seg-save"
                  onClick={handleSavePrefs}
                >
                  Save preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
